import { isoWeekStart, weekday, type IsoDate } from '../dates';
import { isFishDinner, type Recipe } from '../recipe';
import type { Product } from '../types';

/**
 * DE: Zähler der geplanten Fischgerichte je ISO-Woche (Schlüssel: Montag der Woche).
 * EN: Counter of planned fish dinners per ISO week (key: Monday of the week).
 */
export type FishCounts = Map<IsoDate, number>;

/**
 * DE: Bis zu diesem Tag fällige Fischgerichte der Woche; verteilt das Wochenziel gleichmäßig.
 * EN: Fish dinners of the week due by this day; spreads the weekly target evenly.
 */
export function fishDue(date: IsoDate, target: number): number {
  return Math.round((target * weekday(date)) / 7);
}

/**
 * DE: true, wenn das Rezept Fisch enthält und laut Wochenplan gerade ein Fischgericht fällig ist.
 * EN: true if the recipe contains fish and a fish dinner is due by the weekly schedule.
 */
export function wantsFish(
  recipe: Recipe,
  date: IsoDate,
  context: { catalog: ReadonlyMap<string, Product>; target: number; counts: FishCounts },
): boolean {
  const planned = context.counts.get(isoWeekStart(date)) ?? 0;
  return planned < fishDue(date, context.target) && isFishDinner(recipe, context.catalog);
}

/**
 * DE: Ein geplantes Fischgericht in seiner Woche zählen; andere Gerichte zählen nicht.
 * EN: Count a planned fish dinner in its week; other dinners do not count.
 */
export function countFish(
  recipe: Recipe,
  date: IsoDate,
  context: { catalog: ReadonlyMap<string, Product>; counts: FishCounts },
): void {
  if (!isFishDinner(recipe, context.catalog)) return;
  const week = isoWeekStart(date);
  context.counts.set(week, (context.counts.get(week) ?? 0) + 1);
}
